import { EventRegistrationData, EventTicketData } from '../../types';
import {
  getAmountPerInterval,
  getIntervalsToEvent,
} from '../../utils/payments';

export interface RegisterAgentData {
  customerPaymentProfileId: string;
  TicketTypeId: number;
  ticketCount: string;
  totalPay: string;
}

export const shirtSizeOptions = ['SM', 'MD', 'LG', 'XL', '2XL', '3XL'];

export const mealTypeOptions = [
  {
    value: 'Standard Meal',
    description: '',
  },
  {
    value: 'Special Meal',
    description:
      'includes: vegetarian, vegan, gluten-free, and other common dietary accommodations',
  },
];

export const mealNote =
  'We will do our best to accommodate dietary preferences and restrictions; however, we cannot guarantee that all specific dietary needs will be met. If you have a severe allergy or restriction, we recommend planning accordingly.';

export const getSelectionStatus = (params: {
  ticket: EventTicketData | undefined;
  shirtSizes: { [key: number]: string };
  mealTypes: { [key: number]: string };
  ticketCount: string;
}) => {
  const { ticket, shirtSizes, mealTypes, ticketCount } = params;

  // The user has selected a ticket that includes shirts
  // This checks to see if they have selected a shirt size for each ticket
  const needsToSelectShirts: boolean = Boolean(
    ticket &&
      ticket.ReqShirtSize === 1 &&
      !(Object.values(shirtSizes).length === Number(ticketCount)),
  );

  // The user has selected a ticket that includes meals
  // This checks to see if they have selected a meal type for each ticket
  const needsToSelectMeals: boolean = Boolean(
    ticket &&
      ticket.ReqMealType === 1 &&
      !(Object.values(mealTypes).length === Number(ticketCount)),
  );

  return { needsToSelectShirts, needsToSelectMeals };
};

export const checkAvailability = (params: {
  data: EventRegistrationData;
  ticket: EventTicketData | undefined;
}) => {
  const { data, ticket } = params;

  // Count of tickets purchased by user
  const purchasedByUser = data.TicketsPurchasedByUser ?? 0;

  // Some Events may have a seating capacity limit and can only sell a limited number of tickets
  // Check if there is a limit and if there are still tickets available
  const availableTicketsEvent: boolean =
    data.TicketsAvailable === null || data.TicketsAvailable > 0;

  // Check if there is a limit on the number of tickets Agents can purchase and whether they have reached that limit
  const availableTicketsUser: boolean =
    data.PerUserTicketLimit === null ||
    purchasedByUser < data.PerUserTicketLimit;

  // The user can proceed with purchasing tickets for this event
  const canPurchase = availableTicketsEvent && availableTicketsUser;

  // The amount of tickets the user can purchase for this event
  const startLimit = 20;
  const ticketSelectLimits = [startLimit];

  if (canPurchase) {
    // Total tickets remaining for the Event
    if (data.TicketsAvailable !== null) {
      ticketSelectLimits.push(data.TicketsAvailable);
    }
    // Total Tickets remaining that an Agent can Purchase
    if (data.PerUserTicketLimit !== null) {
      ticketSelectLimits.push(data.PerUserTicketLimit - purchasedByUser);
    }
    // Total tickets remaining for the selected ticket type
    if (ticket && ticket.Available !== null) {
      ticketSelectLimits.push(ticket.Available);
    }
  }

  // Find the lowest of the ticket select limit
  const defaultLimit = canPurchase ? Math.min(...ticketSelectLimits) : 0;

  return {
    availableTicketsEvent,
    availableTicketsUser,
    ticketSelectLimits,
    canPurchase,
    startLimit,
    defaultLimit,
  };
};

export const checkPaymentPlan = (params: {
  register: RegisterAgentData;
  ticket: EventTicketData | undefined;
  DateBegin: EventRegistrationData['DateBegin'];
}) => {
  const { register, ticket, DateBegin } = params;

  const ticketCount = Number(register.ticketCount);

  // The amount they are paying today as a number instead of a string
  const payTodayAmount = Number(register.totalPay);

  // The highest amount they could possibly pay: full price for all tickets
  const payTodayMax =
    ticket && ticketCount ? (ticket.Price ?? 0) * ticketCount : 0;

  // The lowest amount they could possibly pay: min price for all tickets
  const payTodayMin =
    ticket && ticketCount ? (ticket.MinPayPerTicket ?? 0) * ticketCount : 0;

  // The difference between the highest amount they could pay and what they are paying
  const payTodayBalance = payTodayMax - payTodayAmount;

  // Number of payment intervals remaining before the event, and the amount due per interval
  const intervalsToEvent = DateBegin ? getIntervalsToEvent({ DateBegin }) : 0;
  const amountPerInterval = DateBegin
    ? getAmountPerInterval({ Balance: payTodayBalance, DateBegin })
    : 0;

  // This ticket type has to be paid in full (min pay equals full price)
  const fullPriceTicket = Boolean(
    ticket && ticket.Price && ticket.MinPayPerTicket
      ? ticket.Price === ticket.MinPayPerTicket
      : false,
  );

  // No intervals left, or the ticket has to be paid in full
  const hasToPayInFull = intervalsToEvent === 0 || fullPriceTicket;
  const isPayingInFull = payTodayMax === payTodayAmount;
  const isPayingAbove = payTodayAmount > payTodayMax;
  const isPayingBelow = payTodayAmount < payTodayMin;

  const displayIntervals =
    !hasToPayInFull && !isPayingInFull && !isPayingAbove && !isPayingBelow;

  return {
    payTodayAmount,
    payTodayMax,
    payTodayMin,
    payTodayBalance,
    intervalsToEvent,
    amountPerInterval,
    fullPriceTicket,
    hasToPayInFull,
    isPayingInFull,
    isPayingAbove,
    isPayingBelow,
    displayIntervals,
  };
};
